"use client";

import React, { useState, useEffect } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import { faAngleLeft, faAngleRight, FontAwesomeIcon } from '@/constants/icons';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { getBaseImageUrl, getFinalDiscountTag, getMerchantHref, getProductDetailHref, discardHTMLTags } from '@/constants/hooks';
import ProductCommentModal from './ProductCommentModal';
import OfferModalTrigger from './OfferModalTrigger';
import OfferOutUrl from '@/components/shared/OfferOutUrl';

interface ProductCarouselProps {
    products: any[];
    companyDomain: string;
    merchant: any;
    mer_slug: string;
    mer_slug_type: string;
}

const ProductCarousel = ({ products, companyDomain, merchant, mer_slug, mer_slug_type }: ProductCarouselProps) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [activeOffer, setActiveOffer] = useState<any | null>(null);
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
        loop: false,
        mode: 'free-snap',
        slides: {
            perView: 4,
            spacing: 24,
        },
        breakpoints: {
            '(max-width: 1280px)': {
                slides: { perView: 3.5, spacing: 20 },
            },
            '(max-width: 1024px)': {
                slides: { perView: 2.5, spacing: 16 },
            },
            '(max-width: 640px)': {
                slides: { perView: 1, spacing: 12 },
            },
        },
        drag: true,
        rubberband: false,
        initial: 0,
        created() {
            setLoaded(true);
        },
        slideChanged(s) {
            setCurrentSlide(s.track.details.rel);
        }
    });

    const merchantHref = getMerchantHref(merchant, mer_slug, mer_slug_type);
    const totalDots = Math.max(0, products.length - 4);

    useEffect(() => {
        const showParam = searchParams.get('show');
        const pIdParam = searchParams.get('p_id');

        if (showParam === 'true' && pIdParam) {
            const matchingProduct = products.find(
                (item) => String(item?.offer?.unique_id) === pIdParam || String(item?.offer?.id) === pIdParam
            );

            if (matchingProduct) {
                setActiveOffer(matchingProduct.offer);
            }
        }

    }, [searchParams, products]);

    const openModal = (offer: any) => {
        setActiveOffer(offer);

        const params = new URLSearchParams(searchParams.toString());
        params.set('show', 'true');
        params.set('p_id', offer?.unique_id);
        router.push(`${pathname}?${params.toString()}`, { scroll: false });
    };

    const closeModal = () => {
        setActiveOffer(null);

        const params = new URLSearchParams(searchParams.toString());
        params.delete('show');
        params.delete('p_id');
        const query = params.toString();
        router.replace(`${pathname}${query ? `?${query}` : ''}`, { scroll: false });
    };

    return (
        <div className="relative w-full group/slider px-1">
            {/* Slider Container */}
            <div ref={sliderRef} className="keen-slider py-4">
                {products.map((item, index) => {
                    const product = item?.offer;
                    const productId = product?.id || index;

                    const originalPrice = product?.original_price ? parseFloat(product?.original_price) : 0;
                    const salePrice = product?.sale_price ? parseFloat(product?.sale_price) : 0;
                    const discountPercent = originalPrice > 0 && salePrice > 0
                        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
                        : null;

                    const finalDiscountTag = getFinalDiscountTag(
                        product?.offer_title || product?.offer_detail,
                        discountPercent,
                    );

                    const type = product?.offer_type?.name;
                    const imageSrc = type === "product"
                        ? getBaseImageUrl(companyDomain, product?.product_image, "")
                        : getBaseImageUrl(companyDomain, merchant?.merchant_logo, "");

                    const isProduct = type === "product";
                    const productDetailUrl = product?.slug
                        ? getProductDetailHref(merchant, mer_slug_type, product?.slug, product?.category?.slug)
                        : '';

                    return (
                        <div key={productId} className="keen-slider__slide h-auto">
                            <div className="group h-full flex flex-col justify-between bg-white rounded-[2rem] shadow-sm hover:shadow-xl hover:shadow-indigo-100/50 transition-all duration-500 overflow-hidden border border-slate-100">

                                {/* Product Image */}
                                <div className="relative aspect-[16/9] w-full bg-indigo-50/50 overflow-hidden block">
                                    <Image
                                        src={imageSrc}
                                        alt={discardHTMLTags(product?.offer_title) || 'Product'}
                                        fill
                                        sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 300px"
                                        className="w-full h-full object-contain p-4 bg-white border border-slate-100 transition-transform duration-500 group-hover:scale-110"
                                    />
                                    {finalDiscountTag && (
                                        <div className="absolute top-2 right-2 z-20 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] sm:text-[11px] font-black bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0 max-w-[80%] truncate">
                                            {finalDiscountTag}
                                        </div>
                                    )}
                                </div>

                                <div className="p-6 pt-2 flex flex-col flex-grow justify-between">
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                {merchant?.merchant_name}
                                            </span>
                                        </div>

                                        {/* Product Title Link */}
                                        {product?.is_detail === 1 && productDetailUrl ? (
                                            <Link href={productDetailUrl} className="block no-underline">
                                                <h3 className="text-sm font-bold text-slate-900 line-clamp-2 mb-2 text-center group-hover:text-indigo-600 transition-colors">
                                                    {discardHTMLTags(product?.offer_title)}
                                                </h3>
                                            </Link>
                                        ) : (
                                            <OfferOutUrl
                                                unique_id={product?.unique_id}
                                                outUrl={product?.url}
                                                merchantHref={merchantHref}
                                                domain={companyDomain}
                                                customClass="block no-underline cursor-pointer"
                                            >
                                                <h3 className="text-sm font-bold text-slate-900 line-clamp-2 mb-2 text-center group-hover:text-indigo-600 transition-colors">
                                                    {discardHTMLTags(product?.offer_title)}
                                                </h3>
                                            </OfferOutUrl>
                                        )}
                                    </div>

                                    <div>
                                        {/* View Details trigger — opens ProductDetailsModal */}
                                        <div className="flex justify-end mb-3">
                                            <OfferModalTrigger
                                                product={product}
                                                merchantName={merchant?.merchant_name || ''}
                                                merchantLogo={getBaseImageUrl(companyDomain, merchant?.merchant_logo, '')}
                                                domain={companyDomain}
                                            >
                                                <span className="text-[11px] font-bold text-slate-900 hover:text-indigo-600 transition-colors tracking-wider cursor-pointer">
                                                    View Details
                                                </span>
                                            </OfferModalTrigger>
                                        </div>

                                        {product?.coupon_code ? (
                                            <OfferOutUrl
                                                unique_id={product?.unique_id}
                                                outUrl={product?.url}
                                                merchantHref={merchantHref}
                                                domain={companyDomain}
                                                customClass="flex items-center justify-center gap-2 w-full bg-[#0F172A] hover:bg-indigo-600 text-white py-3 rounded-xl text-[11px] font-bold tracking-wider transition-all duration-300 shadow-sm group-hover:shadow-indigo-300/50 group-hover:shadow-lg transform hover:-translate-y-0.5 active:scale-95 cursor-pointer"
                                            >
                                                <span>Show Coupon</span>
                                            </OfferOutUrl>
                                        ) : product?.is_detail === 1 && productDetailUrl ? (
                                            <Link
                                                href={productDetailUrl}
                                                className="flex items-center justify-center gap-2 w-full bg-[#0F172A] hover:bg-indigo-600 text-white py-3 rounded-xl text-[11px] font-bold tracking-wider transition-all duration-300 shadow-sm group-hover:shadow-indigo-300/50 group-hover:shadow-lg transform hover:-translate-y-0.5 active:scale-95 cursor-pointer"
                                            >
                                                <span>{isProduct ? 'Buy Now' : 'Grab Deal'}</span>
                                            </Link>
                                        ) : (
                                            <OfferOutUrl
                                                unique_id={product?.unique_id}
                                                outUrl={product?.url}
                                                merchantHref={merchantHref}
                                                domain={companyDomain}
                                                customClass="flex items-center justify-center gap-2 w-full bg-[#0F172A] hover:bg-indigo-600 text-white py-3 rounded-xl text-[11px] font-bold tracking-wider transition-all duration-300 shadow-sm group-hover:shadow-indigo-300/50 group-hover:shadow-lg transform hover:-translate-y-0.5 active:scale-95 cursor-pointer"
                                            >
                                                <span>{isProduct ? 'Buy Now' : 'Grab Deal'}</span>
                                            </OfferOutUrl>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Dots */}
            {loaded && totalDots > 0 && (
                <div className="flex items-center justify-center gap-2 mt-5">
                    {Array.from({ length: totalDots }).map((_, i) => (
                        <div
                            key={i}
                            className={`transition-all duration-300 rounded-full h-2 ${currentSlide - 1 === i
                                ? 'w-2 bg-indigo-600'
                                : 'w-2 bg-slate-300'
                                }`}
                        />
                    ))}
                </div>
            )}

            {/* Arrows */}
            {loaded && slider.current && products.length > 1 && (
                <>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            slider.current?.prev();
                        }}
                        className="absolute left-1 lg:left-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-white hover:bg-indigo-600 hover:text-white text-slate-700 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 z-10 border border-slate-100 opacity-100 translate-x-0 lg:opacity-0 lg:-translate-x-4 lg:group-hover/slider:opacity-100 lg:group-hover/slider:translate-x-0"
                        aria-label="Previous slide"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} className="w-4 h-4" />
                    </button>

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            slider.current?.next();
                        }}
                        className="absolute right-1 lg:right-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-white hover:bg-indigo-600 hover:text-white text-slate-700 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 z-10 border border-slate-100 opacity-100 translate-x-0 lg:opacity-0 lg:translate-x-4 lg:group-hover/slider:opacity-100 lg:group-hover/slider:translate-x-0"
                        aria-label="Next slide"
                    >
                        <FontAwesomeIcon icon={faAngleRight} className="w-4 h-4" />
                    </button>
                </>
            )}

            {/* Single shared modal instance — driven purely by activeOffer state */}
            {activeOffer && (
                <ProductCommentModal
                    product={activeOffer}
                    merchantName={merchant?.merchant_name}
                    merchantLogo={getBaseImageUrl(companyDomain, merchant?.merchant_logo, '')}
                    domain={companyDomain}
                    isOpen={!!activeOffer}
                    onClose={closeModal}
                />
            )}
        </div>
    );
};

export default ProductCarousel;